import { generateShortUuid } from 'ag-common-svc/shared/utils/generate-item-uuid';
import {
  ButtonActionType,
  ButtonNavigateTo,
  HrefTarget,
  LinkButtonActionPayload,
  MailToButtonActionPayload,
} from './template-builder-button-widget.model';

export enum ListLayout {
  list = 'list',
  table = 'table',
  cards = 'cards',
}
export enum ListMarker {
  none = 'none',
  icon = 'icon',
  bullet = 'bullet',
  number = 'number',
}
export type ListActionPayload = LinkButtonActionPayload | MailToButtonActionPayload | { number: string | number };

export class ListWidgetItem {
  id = generateShortUuid();
  title = 'List item';
  description = '';
  icon = 'check';
  actionType: ButtonActionType | null = null;
  actionPayload: ListActionPayload = new LinkButtonActionPayload();
}

export interface ListElementAppearance {
  color?: string;
  size?: number;
}

export class ListWidgetData {
  cardsPerRow = 3;
  cardsGap = 20;
  cardsBackground?: string;
  headingAppearance: ListElementAppearance = {};
  descriptionAppearance: ListElementAppearance = {};
  iconAppearance: ListElementAppearance = {};
  layout = ListLayout.list;
  marker = ListMarker.icon;
  items: ListWidgetItem[] = Array.from({ length: 3 }, (_, index) => {
    const item = new ListWidgetItem();
    item.title = `List item ${index + 1}`;
    return item;
  });
}

/** Native hrefs preserve keyboard navigation, context menus and modified clicks. */
export function getListItemHref(item: ListWidgetItem): string | null {
  if (item?.actionType === ButtonActionType.link) {
    const payload = item.actionPayload as LinkButtonActionPayload;
    if (payload?.navigateTo === ButtonNavigateTo.portal) {
      const page = payload.portalPage?.trim();
      return page?.startsWith('/') && !page.startsWith('//') ? page : null;
    }
    const href = payload?.href?.trim();
    if (!href) return null;
    // Reject executable/unsupported schemes before normalizing protocol-less URLs.
    if (/^[a-z][a-z\d+.-]*:/i.test(href) && !/^https?:\/\//i.test(href)) return null;
    try {
      const url = new URL(/^https?:\/\//i.test(href) ? href : `https://${href.replace(/^\/\//, '')}`);
      return url.hostname.includes('.') || url.hostname === 'localhost' ? url.href : null;
    } catch {
      return null;
    }
  }
  if (item?.actionType === ButtonActionType.mailTo) {
    const payload = item.actionPayload as MailToButtonActionPayload;
    const to = payload?.to?.trim();
    if (!to || !/^[^\s@?&#]+@[^\s@?&#]+\.[^\s@?&#]+$/.test(to)) return null;
    return `mailto:${to}?subject=${encodeURIComponent(payload.subject ?? '')}&body=${encodeURIComponent(payload.body ?? '')}`;
  }
  if (item?.actionType === ButtonActionType.phone) {
    const number = String((item.actionPayload as { number: string | number })?.number ?? '').trim();
    const digits = number.replace(/\D/g, '');
    return /^\+?[\d ()-]+$/.test(number) && digits.length >= 10 && digits.length <= 15
      ? `tel:${number.replace(/[ ()-]/g, '')}`
      : null;
  }
  return null;
}

export function getListItemTarget(item: ListWidgetItem): HrefTarget {
  return item?.actionType === ButtonActionType.link &&
    (item.actionPayload as LinkButtonActionPayload)?.target === HrefTarget.blank
    ? HrefTarget.blank
    : HrefTarget.self;
}

/** Validate persisted content too: its editors may have been closed before Save. */
export function getListWidgetError(data: ListWidgetData): string | null {
  if (!Object.values(ListLayout).includes(data?.layout) || !Object.values(ListMarker).includes(data?.marker)) {
    return 'Choose a layout and marker for the list.';
  }
  if (!Array.isArray(data?.items) || !data.items.length) return 'A list must contain at least one item.';
  for (const [index, item] of data.items.entries()) {
    if (!item?.title?.trim()) return `List item ${index + 1}: enter a heading.`;
    if (data.marker === ListMarker.icon && !item.icon?.trim()) return `List item ${index + 1}: choose an icon.`;
    if (item.actionType && !getListItemHref(item)) return `List item ${index + 1}: enter a valid action destination.`;
  }
  return null;
}
